import { useMemo, useState, useRef, useEffect } from 'react';
import { ReactTags } from 'react-tag-autocomplete';
import SearchableDropdown from '../common/SearchableDropdown';
import ClearableInput from '../common/ClearableInput';
import ClearableDatePicker from '../common/ClearableDatePicker';
import 'react-datepicker/dist/react-datepicker.css';

const TableIcon = () => (
  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 16 16">
    <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2zm15 2h-4v3h4V4zm0 4h-4v3h4V8zm0 4h-4v3h3a1 1 0 0 0 1-1v-2zm-5 3v-3H6v3h4zm-5 0v-3H1v2a1 1 0 0 0 1 1h3zm-4-4h4V8H1v3zm0-4h4V4H1v3zm5-3v3h4V4H6zm4 4H6v3h4V8z" />
  </svg>
);

const BoxIcon = () => (
  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

const DietRequestsFilters = ({
  showFilters,
  setShowFilters,
  filterInputs,
  handleInputChange,
  countries,
  labels,
  profiles,
  isAdmin,
  applyFilters,
  clearAllFilters,
  parseDateString,
  formatDateForAPI,
  onDownload
}) => {
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Transform profiles for SearchableDropdown (no "All Clients" option)
  const clientItems = useMemo(() => {
    const items = [];
    if (profiles) {
      profiles.forEach(profile => {
        items.push({ label: profile.name, value: profile.clientKey });
      });
    }
    return items;
  }, [profiles]);

  // Transform countries for SearchableDropdown (no "All Countries" option)
  const countryItems = useMemo(() => {
    const items = [];
    if (countries) {
      countries.forEach(country => {
        items.push({ label: country.value, value: country.key });
      });
    }
    return items;
  }, [countries]);

  const downloadOptions = [
    { key: 'dietRequests', label: 'Diet Requests', icon: <TableIcon /> },
    { key: 'dietProducts', label: 'Diet Products', icon: <BoxIcon /> },
  ];

  return (
    <div className={isAdmin ? '' : 'lg:max-w-2xl lg:mx-auto'}>
      {/* Filter Toggle and Download Button Row */}
      <div className={`${showFilters ? 'mb-4' : 'mb-2'} flex items-center justify-between`}>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center space-x-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors`}
          data-intro="filter-toggle"
        >
          <svg
            className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
        </button>

        {/* Download Dropdown */}
        {onDownload && (
          <div className="relative" ref={downloadRef}>
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              className="flex items-center space-x-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Download</span>
              <svg className={`w-3 h-3 transition-transform ${showDownloadMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showDownloadMenu && (
              <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
                {['csv', 'xlsx'].map((format, formatIndex) => (
                  <div key={format}>
                    {formatIndex > 0 && <div className="border-t border-gray-200 dark:border-gray-700"></div>}
                    <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{format}</div>
                    {downloadOptions.map((option, optionIndex) => (
                      <button
                        key={`${format}-${option.key}`}
                        onClick={() => { onDownload(option.key, format); setShowDownloadMenu(false); }}
                        className={`w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700${formatIndex === 1 && optionIndex === downloadOptions.length - 1 ? ' rounded-b-lg' : ''}`}
                      >
                        {option.icon}
                        {option.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Collapsible Filter Section */}
      <div
        className={`bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out ${
          showFilters
            ? 'mb-6 max-h-screen opacity-100 p-4 overflow-visible'
            : 'max-h-0 opacity-0 p-0 overflow-hidden'
        }`}
        data-intro="filters-section"
      >
        <div className={`grid grid-cols-1 gap-4 ${isAdmin ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-2 lg:max-w-2xl lg:mx-auto'}`}>
          {/* User Filter - Admin Only */}
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                User
              </label>
              <ClearableInput
                value={filterInputs.user}
                onChange={(value) => handleInputChange('user', value)}
                onClear={() => handleInputChange('user', '')}
                placeholder="Filter by user..."
              />
            </div>
          )}

          {/* Country Filter - Only show when multiple countries available */}
          {countries && countries.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Country
              </label>
              <SearchableDropdown
                items={countryItems}
                onSelect={(selectedItem) => handleInputChange('country', selectedItem.value)}
                onClear={() => handleInputChange('country', '')}
                buttonPlaceholder="Select country"
                widthClass="w-full"
                selectedValue={filterInputs.country}
                searchable={false}
              />
            </div>
          )}

          {/* Client Filter - Admin Only */}
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Client
              </label>
              <SearchableDropdown
                items={clientItems}
                onSelect={(selectedItem) => handleInputChange('profile', selectedItem.value)}
                onClear={() => handleInputChange('profile', '')}
                buttonPlaceholder="Select client"
                searchPlaceholder="Search clients..."
                widthClass="w-full"
                selectedValue={filterInputs.profile}
              />
            </div>
          )}

          {/* From Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              From Date
            </label>
            <ClearableDatePicker
              selected={parseDateString(filterInputs.fromDate)}
              onChange={(date) => {
                handleInputChange('fromDate', formatDateForAPI(date));
              }}
              onClear={() => handleInputChange('fromDate', '')}
              placeholder="Select from date"
              maxDate={parseDateString(filterInputs.toDate)}
            />
          </div>

          {/* To Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              To Date
            </label>
            <ClearableDatePicker
              selected={parseDateString(filterInputs.toDate)}
              onChange={(date) => {
                handleInputChange('toDate', formatDateForAPI(date));
              }}
              onClear={() => handleInputChange('toDate', '')}
              placeholder="Select to date"
              minDate={parseDateString(filterInputs.fromDate)}
            />
          </div>

          {/* Request ID Filter - Admin Only */}
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Request ID
              </label>
              <ClearableInput
                value={filterInputs.recommendationId}
                onChange={(value) => handleInputChange('recommendationId', value)}
                onClear={() => handleInputChange('recommendationId', '')}
                placeholder="Filter by request ID..."
              />
            </div>
          )}
        </div>

        {/* Labels Filter - Full Width - Admin Only */}
        {isAdmin && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Labels
            </label>
            <ReactTags
              selected={Array.isArray(filterInputs.labels) ? filterInputs.labels.map(label => ({
                value: label.id,
                label: label.name
              })) : []}
              suggestions={labels ? labels.map(label => ({
                value: label.id,
                label: label.name
              })) : []}
              onAdd={(newTag) => {
                const labelObj = labels.find(l => l.id === newTag.value);
                if (labelObj) {
                  const currentLabels = Array.isArray(filterInputs.labels) ? filterInputs.labels : [];
                  const isAlreadySelected = currentLabels.some(l => l.id === labelObj.id);
                  if (!isAlreadySelected) {
                    handleInputChange('labels', [...currentLabels, labelObj]);
                  }
                }
              }}
              onDelete={(tagIndex) => {
                const currentLabels = Array.isArray(filterInputs.labels) ? filterInputs.labels : [];
                const newLabels = currentLabels.filter((_, index) => index !== tagIndex);
                handleInputChange('labels', newLabels);
              }}
              allowNew={false}
              placeholderText="Select labels..."
            />
          </div>
        )}

        {/* Test Filter - Full Width - Admin Only */}
        {isAdmin && (
          <div className="mt-4">
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={filterInputs.test}
                onChange={e => handleInputChange('test', e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <span>Tests Only</span>
            </label>
          </div>
        )}

        {/* Filter Action Buttons */}
        <div className="mt-4 flex justify-center space-x-3" data-intro="filter-buttons">
          <button
            onClick={applyFilters}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span>Filter</span>
          </button>
          <button
            onClick={clearAllFilters}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-white dark:focus:ring-gray-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span>Clear</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DietRequestsFilters;
